import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Calculator, Lock, Save } from 'lucide-react';
import { allocationsApi, stockApi } from '@/features/stock/api';
import { eventsApi } from '@/features/events/api';
import { horasEntre } from '@/features/events/duracion';
import { expectedApi, merchPlannerApi } from './api';
import { ceilSafe, expectedPayload, plan, type PlanInput, type PlanNeed } from './engine';
import { dataState, effectiveHours, poolsFromCatalog, poolsFromEvent, saveBlock } from './pools';
import { DEFAULT_DRAFT, draftKey, useMerchPlanDrafts, withDefaults, type DraftField } from './planDraft';
import { queryKeys } from '@/lib/api/queryKeys';
import { useSelectedBrandId } from '@/lib/branding/brandContextStore';
import { useAuth } from '@/lib/auth/useAuth';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Collapsible } from '@/components/ui/Collapsible';
import { PercentSlider } from '@/components/ui/PercentSlider';
import { ChipGroup } from '@/components/ui/ChipGroup';
import { Stepper } from '@/components/ui/Stepper';
import { DigitalClock } from '@/components/ui/DigitalClock';
import { PeopleRow } from '@/components/ui/PeopleRow';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';
import { usePrefsStore } from '@/lib/prefs/usePrefsStore';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import type { XkEvent, XkEventGroup } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { OrderSplit, type RarityNote } from './OrderSplit';
import { RushHourSimulator } from './RushHourSimulator';

/**
 * Merch planner (antes la calculadora, CH-09) — de los invitados al pedido por premio,
 * con la cuenta a la vista, por estación como el juego. Sirve sin evento (una propuesta) y
 * con evento (`?event=<id>`): entonces sólo cuenta lo que juega allí, y «guardar» escribe el
 * pedido en las asignaciones del evento.
 */
export function MerchPlannerPage() {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  useFavoritable(t('merchPlanner.title'));
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const selectedBrand = useSelectedBrandId();

  // ── El evento de la URL ──
  const rawEvent = Number(params.get('event'));
  const urlEventId = Number.isInteger(rawEvent) && rawEvent > 0 ? rawEvent : null;
  const eventQ = useQuery({
    queryKey: queryKeys.events.detail(urlEventId ?? 0),
    queryFn: () => eventsApi.getEvent(urlEventId as number),
    enabled: urlEventId != null,
  });
  const eventPending = urlEventId != null && eventQ.isPending;
  const eventFailed = urlEventId != null && eventQ.isError;
  const eventMode = urlEventId != null && eventQ.isSuccess;
  const eventId = eventMode ? urlEventId : null;
  const event = eventMode ? eventQ.data : undefined;

  // La marca del plan sale del evento cargado. El reinicio por cambio de marca se engancha al
  // selector (no a esta marca derivada): si no, la llegada del evento lo borraría (D-21).
  const planBrandId = event?.companyId ?? selectedBrand ?? user?.companyId ?? null;

  const [excluded, setExcluded] = useState<Set<number>>(new Set());
  const [groupId, setGroupId] = useState<number | ''>('');
  const lastBrand = useRef(selectedBrand);
  useEffect(() => {
    if (lastBrand.current === selectedBrand) return;
    const before = lastBrand.current;
    lastBrand.current = selectedBrand;
    setGroupId('');
    setExcluded(new Set());
    // Sólo un cambio real de marca (no la primera carga del selector) suelta el evento de otra marca.
    if (before != null && eventQ.data && eventQ.data.companyId !== selectedBrand) {
      setParams(
        (p) => {
          const next = new URLSearchParams(p);
          next.delete('event');
          return next;
        },
        { replace: true },
      );
    }
  }, [selectedBrand]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedGroup: number | '' = event?.eventGroupId ?? groupId;

  // ── Selectores proyecto / evento ──
  const groupsQ = useQuery({
    queryKey: queryKeys.eventGroups.byBrand(planBrandId ?? 0),
    queryFn: () => eventsApi.listGroups({ companyId: planBrandId }),
    enabled: !!planBrandId && !eventPending,
  });
  const eventsQ = useQuery({
    queryKey: queryKeys.events.byGroup(Number(selectedGroup) || 0),
    queryFn: () => eventsApi.listEvents({ eventGroupId: Number(selectedGroup) }),
    enabled: selectedGroup !== '' && !eventPending,
  });
  const groupInList = event != null && (groupsQ.data?.items ?? []).some((g) => g.id === event.eventGroupId);
  const groupQ = useQuery({
    queryKey: queryKeys.eventGroups.detail(event?.eventGroupId ?? 0),
    queryFn: () => eventsApi.getGroup(event?.eventGroupId as number),
    enabled: event != null && groupsQ.isSuccess && !groupInList,
  });
  const groupOptions: XkEventGroup[] = [...(groupsQ.data?.items ?? [])];
  if (event && !groupInList && groupQ.data) groupOptions.push(groupQ.data);
  const eventOptions: XkEvent[] = [...(eventsQ.data?.items ?? [])];
  if (event && !eventOptions.some((e) => e.id === event.id)) eventOptions.push(event);

  // ── Lecturas completas, con claves propias (no las páginas de 25) ──
  const stocksQ = useQuery({
    queryKey: queryKeys.stock.byBrandAll(planBrandId ?? 0),
    queryFn: () => stockApi.list({ companyId: planBrandId }, { perPage: 500 }),
    enabled: !!planBrandId && !eventPending,
  });
  const stationsQ = useQuery({
    queryKey: queryKeys.stations.byEventAll(eventId ?? 0),
    queryFn: () => merchPlannerApi.stationsOfEvent(eventId as number),
    enabled: eventMode,
  });
  const allocsQ = useQuery({
    queryKey: queryKeys.stockAllocations.byEventAll(eventId ?? 0),
    queryFn: () => allocationsApi.list({ eventId: eventId as number }, { perPage: 500 }),
    enabled: eventMode,
  });
  // Con evento, nada se da por bueno sin las tres lecturas; sin marca ni evento no hay nada que leer.
  const data = eventPending
    ? 'loading'
    : eventMode
      ? dataState([stocksQ, stationsQ, allocsQ])
      : !planBrandId
        ? 'ok'
        : dataState([stocksQ]);

  const stocks = useMemo(() => stocksQ.data?.items ?? [], [stocksQ.data]);
  const fromEvent = useMemo(
    () =>
      eventMode && data === 'ok'
        ? poolsFromEvent(stationsQ.data?.items ?? [], allocsQ.data?.items ?? [], stocks)
        : null,
    [eventMode, data, stationsQ.data, allocsQ.data, stocks],
  );

  // ── Entradas (borrador por marca y evento) ──
  const key = planBrandId ? draftKey(planBrandId, eventId) : null;
  const stored = useMerchPlanDrafts((s) => (key ? s.drafts[key] : undefined));
  const setStored = useMerchPlanDrafts((s) => s.setField);
  const draft = key ? withDefaults(stored) : DEFAULT_DRAFT;
  const setDraft = (name: DraftField) => (value: string) => {
    if (key) setStored(key, name, value);
  };
  const eventHours = event?.startDate && event.endDate ? horasEntre(event.startDate, event.endDate) : null;
  const hoursView = eventMode
    ? effectiveHours(stored?.hours ?? '', eventHours)
    : { value: draft.hours, fromEvent: false, multiDay: null };

  const catalogStations = num(draft.stations);
  const pools = useMemo(() => {
    if (data !== 'ok') return [];
    if (eventMode) return fromEvent?.pools ?? [];
    return poolsFromCatalog(stocks, excluded, catalogStations ?? 0).pools;
  }, [data, eventMode, fromEvent, stocks, excluded, catalogStations]);

  const input: PlanInput = {
    events: eventMode ? 1 : num(draft.events),
    guests: num(draft.guests),
    shareWhoPlay: pctOf(draft.shareWhoPlay),
    claimRate: pctOf(draft.claimRate),
    buffer: pctOf(draft.buffer),
    hours: num(hoursView.value),
    minutesPerHandout: num(draft.minutesPerHandout),
  };
  const result = plan(input, pools);

  const block = saveBlock({ eventId, eventLoaded: eventQ.isSuccess, data, result });
  const save = useMutation({
    mutationFn: () => {
      if (block !== null || eventId == null || !fromEvent) return Promise.reject(new Error('pending'));
      return expectedApi.set(eventId, expectedPayload(result, fromEvent.allocatedStockIds, 'toOrder'));
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: queryKeys.insights.all });
      void qc.invalidateQueries({ queryKey: queryKeys.eventReport.all });
      void qc.invalidateQueries({ queryKey: queryKeys.liveMonitor.all });
      void qc.invalidateQueries({ queryKey: queryKeys.stockAllocations.byEvent(eventId ?? 0) });
      toast({
        tone: 'success',
        title: t('merchPlanner.savedToast', { n: res.updated }),
        description: res.skipped.length ? t('merchPlanner.savedSkipped', { n: res.skipped.length }) : undefined,
      });
    },
    onError: (e: Error) => toast({ tone: 'error', title: t('merchPlanner.saveFailed'), description: e.message }),
  });

  const stockById = new Map(stocks.map((s) => [s.id, s]));
  const stationName = new Map((stationsQ.data?.items ?? []).map((s) => [s.id, s.name]));
  const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;

  // Lo que se enseña en «Premios en el juego» (con evento): rareza por estación, cadencia.
  const prizeList = useMemo(() => {
    if (!fromEvent) return [];
    const byStock = new Map<number, { name: string; min: number; max: number; hasWeight: boolean; cooldown: number | null }>();
    for (const s of fromEvent.slots) {
      const name = stockById.get(s.stockId)?.name ?? String(s.stockId);
      const cur = byStock.get(s.stockId) ?? { name, min: Infinity, max: -Infinity, hasWeight: false, cooldown: null };
      if (s.prizeMode === 'cooldown') cur.cooldown = s.cooldownMinutes ?? cur.cooldown ?? 0;
      else {
        cur.hasWeight = true;
        cur.min = Math.min(cur.min, s.rating);
        cur.max = Math.max(cur.max, s.rating);
      }
      byStock.set(s.stockId, cur);
    }
    return [...byStock.entries()].map(([stockId, v]) => ({ stockId, ...v }));
  }, [fromEvent, stocks]); // eslint-disable-line react-hooks/exhaustive-deps


  const [workingOpen, setWorkingOpen] = useState(false);
  const [simCrowd, setSimCrowd] = useState<'steady' | 'rush'>('rush');
  const fmt = (n: number) => n.toLocaleString(language);
  // Lecturas de los controles (el borrador guarda textos; un valor fuera de la rejilla se enseña tal cual, CA-5).
  const guestsNum = num(draft.guests);
  const shareNum = num(draft.shareWhoPlay);
  const claimNum = num(draft.claimRate);
  const bufferNum = num(draft.buffer);
  const minutesNum = num(draft.minutesPerHandout);
  const eventsNum = num(draft.events);
  const clockSeconds = minutesNum == null ? null : Math.round(minutesNum * 60);
  const perPointHour = minutesNum != null && minutesNum > 0 ? Math.floor(60 / minutesNum) : null;
  const shareWords =
    shareNum == null
      ? null
      : shareNum <= 25
        ? t('merchPlanner.shareWords.few')
        : shareNum <= 50
          ? t('merchPlanner.shareWords.typical', { n: Math.round(shareNum / 10) })
          : t('merchPlanner.shareWords.most');
  const eventHoursView = eventMode ? effectiveHours('', eventHours) : null;
  const hoursChip = eventHoursView?.fromEvent ? eventHoursView.value : null;
  const hoursChoice = hoursChip != null && hoursView.fromEvent ? 'event' : hoursView.value;
  const hoursOptions = [
    ...(hoursChip != null ? [{ value: 'event', label: t('merchPlanner.hoursEventChip', { h: hoursChip }) }] : []),
    ...[2, 3, 4, 6, 8, 10].map((h) => ({ value: String(h), label: t('merchPlanner.hoursChip', { n: h }) })),
  ];
  const startMinutes = (() => {
    const d = eventMode && event?.startDate ? new Date(event.startDate) : null;
    return d && !Number.isNaN(d.getTime()) ? d.getHours() * 60 + d.getMinutes() : 12 * 60;
  })();
  const rarityNotes = new Map<number, RarityNote>(prizeList.map((p) => [p.stockId, { hasWeight: p.hasWeight, cooldown: p.cooldown }]));
  const addStationsHref = event ? `/projects/${event.eventGroupId}?tab=plan&focusEvent=${event.id}` : undefined;

  const noMerchInCatalog = !eventMode && data === 'ok' && poolsFromCatalog(stocks, new Set(), 1).pools[0].slots.length === 0;
  const loadFailed = data === 'error';
  const tooLong = data === 'tooLong';

  const selectCls =
    'h-10 rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-input)] px-3 text-sm disabled:opacity-60';
  const setEvent = (next: number | '') =>
    setParams(
      (p) => {
        const q = new URLSearchParams(p);
        if (next === '') q.delete('event');
        else q.set('event', String(next));
        return q;
      },
      { replace: true },
    );


  const stationsReadout =
    data === 'ok' ? (
      <div className="flex flex-col gap-2" data-testid="planner-stations-locked">
        <span className="text-sm font-medium text-[var(--color-fg-primary)]">{t('merchPlanner.stationsLabel')}</span>
        <div className="flex items-center gap-2.5">
          <span className="font-[var(--font-dash-mono)] text-[26px] font-bold tabular-nums text-[var(--color-fg-primary)]">{pools.length}</span>
          <Lock className="h-4 w-4 text-[var(--color-fg-muted)]" aria-hidden="true" />
        </div>
        <span className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.stationsLockedBa')}</span>
      </div>
    ) : null;

  const measured = result.state === 'MEASURED' ? result : null;
  const cap = measured && measured.capacity.state === 'MEASURED' ? measured.capacity : null;

  return (
    <div className="page space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--color-fg-primary)]">
            <Calculator className="h-6 w-6 text-[var(--color-accent-ink)]" />
            {t('merchPlanner.title')}
            <HelpHint section="merch-planner" />
          </h1>
          <p className="mt-1 text-sm text-[var(--color-fg-muted)]">{t('merchPlanner.subtitle')}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-3">
            <span className="text-[13px] text-[var(--color-fg-muted)]">{t('merchPlanner.savedOnDevice')}</span>
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={block !== null}>
              <Save className="mr-2 h-4 w-4" />
              {t('merchPlanner.saveCta')}
            </Button>
          </div>
          {eventId == null ? (
            <p className="text-[11px] text-[var(--color-fg-subtle)]">{t('merchPlanner.saveNeedsEvent')}</p>
          ) : (
            <p className="max-w-md text-right text-[11px] text-[var(--color-fg-subtle)]">
              {t('merchPlanner.saveBody')}
              {fromEvent?.savedExpected != null ? ` ${t('merchPlanner.savedNow', { n: fromEvent.savedExpected })}` : ''}
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[400px_minmax(0,1fr)]">
        {/* ── Entradas ── */}
        <div className="space-y-4">
          <Card>
            <CardContent className="space-y-2 pt-5">
              <p className="text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">{t('merchPlanner.planFor')}</p>
              <p className="text-[11px] text-[var(--color-fg-subtle)]">{t('merchPlanner.planForHint')}</p>
              {eventFailed ? (
                <p className="text-xs text-[var(--color-error)]" role="alert" data-testid="planner-event-not-found">
                  {t('merchPlanner.eventNotFound')}
                </p>
              ) : null}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <select
                  value={selectedGroup}
                  onChange={(e) => {
                    setGroupId(e.target.value === '' ? '' : Number(e.target.value));
                    setEvent('');
                  }}
                  aria-label={t('merchPlanner.project')}
                  className={selectCls}
                >
                  <option value="">{t('merchPlanner.pickProject')}</option>
                  {groupOptions.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                <select
                  value={eventId ?? ''}
                  onChange={(e) => setEvent(e.target.value === '' ? '' : Number(e.target.value))}
                  disabled={selectedGroup === ''}
                  aria-label={t('merchPlanner.event')}
                  className={selectCls}
                >
                  <option value="">{t('merchPlanner.noEvent')}</option>
                  {eventOptions.map((ev) => (
                    <option key={ev.id} value={ev.id}>{ev.name}</option>
                  ))}
                </select>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-6 pt-5">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold text-[var(--color-fg-primary)]">{t('merchPlanner.crowdTitle')}</h2>
                {guestsNum != null && guestsNum > 0 ? (
                  <span className="font-[var(--font-dash-mono)] text-xs text-[var(--color-fg-muted)]">
                    {t('merchPlanner.guestsInAll', { n: fmt((eventMode ? 1 : (eventsNum ?? 1)) * guestsNum) })}
                  </span>
                ) : null}
              </div>
              <div className="space-y-2.5">
                <Stepper
                  id="mp-guests"
                  label={t('merchPlanner.guests')}
                  value={draft.guests}
                  onChange={setDraft('guests')}
                  step={50}
                  min={1}
                  max={100000}
                  decrementLabel={t('merchPlanner.guestsFewer')}
                  incrementLabel={t('merchPlanner.guestsMore')}
                  placeholder="300"
                  hint={t('merchPlanner.guestsHint')}
                />
                <ChipGroup
                  label={t('merchPlanner.guests')}
                  value={guestsNum}
                  onChange={(v) => setDraft('guests')(String(v))}
                  options={[100, 300, 1000, 5000].map((g) => ({ value: g, label: fmt(g) }))}
                />
              </div>
              <PercentSlider
                id="mp-share"
                label={t('merchPlanner.shareWhoPlay')}
                value={shareNum}
                min={5}
                max={100}
                step={5}
                onChange={(v) => setDraft('shareWhoPlay')(String(v))}
                hint={
                  <>
                    {shareWords}
                    <span className="mt-1 block text-[11px] text-[var(--color-fg-subtle)]">{t('merchPlanner.shareWhoPlayHint')}</span>
                  </>
                }
              >
                <PeopleRow total={10} filled={shareNum == null ? 0 : Math.round(shareNum / 10)} />
              </PercentSlider>
              <PercentSlider
                id="mp-claim"
                label={t('merchPlanner.claimLabel')}
                value={claimNum}
                min={50}
                max={100}
                step={5}
                onChange={(v) => setDraft('claimRate')(String(v))}
                hint={t('merchPlanner.claimRateHint')}
              />
              <div className="flex flex-col gap-2.5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-[var(--color-fg-primary)]">{t('merchPlanner.bufferLabel')}</span>
                  <span className="font-[var(--font-dash-mono)] text-[28px] font-bold leading-none tabular-nums text-[var(--color-fg-primary)]">
                    {bufferNum == null ? '—' : `+${bufferNum}%`}
                  </span>
                </div>
                <ChipGroup
                  label={t('merchPlanner.bufferLabel')}
                  value={bufferNum}
                  onChange={(v) => setDraft('buffer')(String(v))}
                  layout="grid"
                  size="lg"
                  options={[
                    { value: 0, label: '0%', sublabel: t('merchPlanner.buffer.tight') },
                    { value: 10, label: '10%', sublabel: t('merchPlanner.buffer.lean') },
                    { value: 15, label: '15%', sublabel: t('merchPlanner.buffer.safe') },
                    { value: 25, label: '25%', sublabel: t('merchPlanner.buffer.generous') },
                  ]}
                />
                <span className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.bufferHint')}</span>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-5 pt-5">
              <h2 className="text-lg font-bold text-[var(--color-fg-primary)]">{t('merchPlanner.dayTitle')}</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {!eventMode ? (
                  <Stepper
                    id="mp-events"
                    label={t('merchPlanner.events')}
                    value={draft.events}
                    onChange={setDraft('events')}
                    step={1}
                    min={1}
                    max={200}
                    decrementLabel={t('merchPlanner.eventsLess')}
                    incrementLabel={t('merchPlanner.eventsMore')}
                  />
                ) : null}
                {eventMode ? (
                  stationsReadout
                ) : (
                  <Stepper
                    id="mp-stations"
                    label={t('merchPlanner.stationsLabel')}
                    value={draft.stations}
                    onChange={setDraft('stations')}
                    step={1}
                    min={1}
                    max={99}
                    decrementLabel={t('merchPlanner.stationsLess')}
                    incrementLabel={t('merchPlanner.stationsMore')}
                    hint={t('merchPlanner.stationsBaHint')}
                  />
                )}
              </div>
              <div className="flex flex-col gap-2.5">
                <Stepper
                  id="mp-hours"
                  label={t('merchPlanner.hoursOpen')}
                  value={hoursView.value}
                  onChange={setDraft('hours')}
                  step={1}
                  min={1}
                  max={24}
                  decrementLabel={t('merchPlanner.hoursLess')}
                  incrementLabel={t('merchPlanner.hoursMore')}
                  placeholder="8"
                  inputMode="decimal"
                  hint={hoursView.multiDay != null ? t('merchPlanner.hoursMultiDay', { h: hoursView.multiDay }) : undefined}
                />
                <ChipGroup
                  label={t('merchPlanner.hoursOpen')}
                  value={hoursChoice}
                  onChange={(v) => setDraft('hours')(v === 'event' ? '' : v)}
                  options={hoursOptions}
                />
              </div>
              <DigitalClock
                label={t('merchPlanner.timePerHandout')}
                seconds={clockSeconds}
                onChange={(s) => setDraft('minutesPerHandout')(String(Math.round((s / 60) * 1e4) / 1e4))}
                labels={{
                  minuteUp: t('merchPlanner.clock.minuteUp'),
                  minuteDown: t('merchPlanner.clock.minuteDown'),
                  secondUp: t('merchPlanner.clock.secondUp'),
                  secondDown: t('merchPlanner.clock.secondDown'),
                }}
                caption={t('merchPlanner.clock.caption')}
                valueText={(m, s) => t('merchPlanner.clock.value', { m, s })}
                hint={perPointHour != null ? t('merchPlanner.clock.hint', { n: perPointHour }) : t('merchPlanner.minutesPerHandoutHint')}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4 pt-5">
            <div>
              <p className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">{t('merchPlanner.prizes')}</p>
              {data === 'loading' ? (
                <Skeleton className="h-24" />
              ) : loadFailed ? (
                <p className="text-xs text-[var(--color-error)]" role="alert" data-testid="planner-load-error">
                  {t('merchPlanner.loadError')}
                </p>
              ) : tooLong ? (
                <p className="text-xs text-[var(--color-fg-muted)]" role="alert" data-testid="planner-too-long">
                  {t('merchPlanner.listTooLong')}
                </p>
              ) : noMerchInCatalog ? (
                <p className="text-xs text-[var(--color-fg-muted)]">
                  {t('merchPlanner.noMerch')}{' '}
                  <Link to="/stock" className="font-semibold text-[var(--color-accent-ink)] hover:underline">{t('stock.list.title')}</Link>
                </p>
              ) : eventMode ? (
                <>
                  {prizeList.length === 0 ? (
                    <p className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.need.items')}</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {prizeList.map((p) => (
                        <li key={p.stockId} className="flex items-center gap-2 text-sm">
                          <span className="flex-1 truncate">{p.name}</span>
                          <span className="text-[11px] tabular-nums text-[var(--color-fg-subtle)]">
                            {[
                              p.hasWeight
                                ? p.min === p.max
                                  ? t('merchPlanner.rarity', { n: p.min })
                                  : t('merchPlanner.rarityRange', { min: p.min, max: p.max })
                                : null,
                              p.cooldown != null && p.cooldown > 0 ? t('merchPlanner.cooldown', { n: p.cooldown }) : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {fromEvent?.duplicates.map((d) => (
                    <p key={`${d.stationId}:${d.stockId}`} className="mt-2 text-[11px] text-[var(--color-warn-ink)]">
                      <span className="font-semibold">{stockById.get(d.stockId)?.name}</span>
                      {' · '}
                      {t('merchPlanner.duplicateRows', { n: d.rows, station: stationName.get(d.stationId) ?? d.stationId })}
                    </p>
                  ))}
                  {fromEvent?.emptyStations.map((s) => (
                    <p key={s.id} className="mt-2 text-[11px] text-[var(--color-warn-ink)]">
                      {t('merchPlanner.stationNoMerch', { station: s.name })}
                    </p>
                  ))}
                </>
              ) : (
                <ul className="space-y-1.5">
                  {stocks
                    .filter((s) => s.status === undefined || s.status === 'Enable')
                    .map((s) => (
                      <li key={s.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!excluded.has(s.id)}
                          onChange={(e) =>
                            setExcluded((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.delete(s.id);
                              else next.add(s.id);
                              return next;
                            })
                          }
                          className="h-4 w-4 accent-[var(--color-xk-accent)]"
                          aria-label={s.name}
                        />
                        <span className={cn('flex-1 truncate', excluded.has(s.id) && 'text-[var(--color-fg-subtle)] line-through')}>{s.name}</span>
                        <span className="text-[11px] tabular-nums text-[var(--color-fg-subtle)]">{t('merchPlanner.rarity', { n: s.rating ?? 3 })}</span>
                      </li>
                    ))}
                </ul>
              )}
            </div>

            {event && data === 'ok' && fromEvent && fromEvent.notInGame.length > 0 ? (
              <div className="rounded-lg border border-dashed border-[var(--color-border-default)] p-3" data-testid="planner-left-out">
                <p className="mb-2 text-[11px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-muted)]">{t('merchPlanner.notInGame.title')}</p>
                <ul className="space-y-1 text-xs text-[var(--color-fg-muted)]">
                  {fromEvent.notInGame.map((o) => (
                    <li key={o.stockId}>
                      <span className="font-semibold text-[var(--color-fg-primary)]">{o.name}</span>
                      {' · '}
                      {t(NOT_IN_GAME_KEY[o.reason])}
                      {o.reason === 'removed' ? (
                        <span className="mt-0.5 block text-[11px] text-[var(--color-fg-subtle)]">{t('merchPlanner.notInGame.disabledStillPlays')}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/projects/${event.eventGroupId}?tab=plan&focusEvent=${event.id}`}
                  className="mt-2 inline-block text-xs font-semibold text-[var(--color-accent-ink)] hover:underline"
                >
                  {t('planner.alloc.drawer.open')}
                </Link>
              </div>
            ) : null}
            </CardContent>
          </Card>
        </div>

        {/* ── Salidas ── */}
        <div className="space-y-6">
          {data === 'loading' ? (
            <Card>
              <CardContent className="py-8">
                <Skeleton className="h-24" />
              </CardContent>
            </Card>
          ) : loadFailed ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-xs text-[var(--color-fg-muted)]" role="alert">{t('merchPlanner.loadError')}</p>
              </CardContent>
            </Card>
          ) : tooLong ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-xs text-[var(--color-fg-muted)]">{t('merchPlanner.listTooLong')}</p>
              </CardContent>
            </Card>
          ) : !measured ? (
            <div
              data-surface="night"
              data-testid="planner-order-total"
              className="rounded-[18px] bg-[var(--color-xk-bg-nav)] p-6 text-[var(--color-fg-primary)]"
            >
              <span className="text-[13px] uppercase tracking-[1px] text-[var(--color-fg-muted)]">{t('merchPlanner.result.order')}</span>
              <p className="font-[var(--font-dash-mono)] text-[64px] font-bold leading-none text-[var(--color-accent-ink)]">—</p>
              <p className="mt-3 text-sm font-semibold">{t('merchPlanner.pendingTitle')}</p>
              <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
                {t('merchPlanner.pendingBody', { needs: result.state === 'PENDING' ? result.needs.map((n) => t(NEED_KEY[n])).join(' · ') : '' })}
              </p>
            </div>
          ) : (
            <>
              <OrderSplit result={measured} eventMode={eventMode} notes={rarityNotes} />

              <p className="text-xs text-[var(--color-fg-muted)]" data-testid="planner-capacity">
                {measured.capacity.state === 'MEASURED'
                  ? t(measured.capacity.covered ? 'merchPlanner.capacity.avgOk' : 'merchPlanner.capacity.avgShort', {
                      points: measured.capacity.points,
                      capacity: measured.capacity.capacityPerHour,
                      peak: measured.capacity.peakPlayersPerHour,
                      extra: measured.capacity.extraPoints,
                    })
                  : t('merchPlanner.capacity.pending', { needs: measured.capacity.needs.map((n) => t(NEED_KEY[n])).join(' · ') })}
              </p>
              <p className="text-xs text-[var(--color-fg-subtle)]">
                {measured.restock.state === 'MEASURED' ? t('merchPlanner.restockGuide') : t('merchPlanner.restockPending')}
              </p>

              {/* La cuenta, no sólo el número. */}
              <Card>
                <CardContent className="pt-5">
                  <Collapsible open={workingOpen} onOpenChange={setWorkingOpen} label={t('merchPlanner.workingToggle', { n: fmt(measured.toOrder) })}>
                    <div className="space-y-1 font-[var(--font-dash-mono)] text-xs text-[var(--color-fg-muted)]">
                      <p>{t('merchPlanner.working.players', { events: measured.working.events, guests: measured.working.guests, share: pct(measured.working.shareWhoPlay), players: measured.players })}</p>
                      <p>{t('merchPlanner.working.claims', { players: measured.players, claim: pct(measured.working.claimRate), claims: measured.claims })}</p>
                      <p>{t('merchPlanner.working.order', { claims: measured.claims, buffer: pct(measured.working.buffer), order: measured.toOrder })}</p>
                      <p>{t('merchPlanner.working.chance')}</p>
                      <p>{t('merchPlanner.working.split')}</p>
                    </div>
                  </Collapsible>
                </CardContent>
              </Card>

              {cap && measured.working.hours != null && minutesNum != null ? (
                <RushHourSimulator
                  key={[
                    Math.round(measured.working.guests * measured.working.shareWhoPlay),
                    Math.round(measured.working.hours * 60),
                    minutesNum,
                    cap.points,
                    ceilSafe(measured.toOrder / measured.working.events),
                    measured.working.claimRate,
                  ].join('|')}
                  crowd={simCrowd}
                  onCrowdChange={setSimCrowd}
                  capacity={cap}
                  players={Math.round(measured.working.guests * measured.working.shareWhoPlay)}
                  dayMinutes={Math.max(1, Math.round(measured.working.hours * 60))}
                  minutesPerHandout={minutesNum}
                  claimRate={measured.working.claimRate}
                  orderPerEvent={ceilSafe(measured.toOrder / measured.working.events)}
                  startMinutes={startMinutes}
                  onAddStations={eventMode ? undefined : (n) => setDraft('stations')(String(Math.min(99, (catalogStations ?? 0) + n)))}
                  addStationsHref={eventMode ? addStationsHref : undefined}
                />
              ) : (
                <div data-surface="night" data-testid="planner-sim" className="rounded-[18px] bg-[var(--color-xk-bg-nav)] p-5 text-[var(--color-fg-primary)]">
                  <h2 className="text-lg font-bold">{t('merchPlanner.sim.title')}</h2>
                  <p className="mt-2 text-sm text-[var(--color-fg-muted)]">
                    {t('merchPlanner.sim.pending', {
                      needs: measured.capacity.state === 'PENDING' ? measured.capacity.needs.map((n) => t(NEED_KEY[n])).join(' · ') : '',
                    })}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const NOT_IN_GAME_KEY = {
  notAllocated: 'merchPlanner.notInGame.unallocated',
  hidden: 'merchPlanner.notInGame.hidden',
  removed: 'merchPlanner.notInGame.disabled',
} as const;

const NEED_KEY: Record<PlanNeed, string> = {
  events: 'merchPlanner.need.events',
  guests: 'merchPlanner.need.guests',
  shareWhoPlay: 'merchPlanner.need.shareWhoPlay',
  claimRate: 'merchPlanner.need.claimRate',
  buffer: 'merchPlanner.need.buffer',
  stations: 'merchPlanner.need.stations',
  items: 'merchPlanner.need.items',
  hours: 'merchPlanner.need.hours',
  minutesPerHandout: 'merchPlanner.need.minutesPerHandout',
};

function num(v: string): number | null {
  const n = Number(v.replace(',', '.'));
  return v.trim() === '' || !Number.isFinite(n) ? null : n;
}

/** Un porcentaje tecleado (40) como fracción (0,4). */
function pctOf(v: string): number | null {
  const n = num(v);
  return n == null ? null : n / 100;
}
